import dayjs from 'dayjs';
import * as Clipboard from 'expo-clipboard';

/**
 * Determines if a given date is in Daylight Saving Time (Summer Time).
 * Compares the timezone offset of the date with January (winter) offset.
 *
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is in DST (summer time)
 */
export const isDaylightSavingTime = (date) => {
  const jan = new Date(date.getFullYear(), 0, 1); // January 1st (winter)
  const jul = new Date(date.getFullYear(), 6, 1); // July 1st (summer)

  // Get the max offset (standard time has larger offset in minutes, e.g., -60 for CET)
  // getTimezoneOffset returns minutes, and is inverted (UTC+1 = -60)
  const standardOffset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  const dateOffset = date.getTimezoneOffset();

  // If current offset is less than standard, we're in DST
  return dateOffset < standardOffset;
};

/**
 * Checks if the user's timezone observes Daylight Saving Time.
 *
 * @returns {boolean} - True if the timezone has DST
 */
export const timezoneHasDST = () => {
  const jan = new Date(new Date().getFullYear(), 0, 1);
  const jul = new Date(new Date().getFullYear(), 6, 1);
  return jan.getTimezoneOffset() !== jul.getTimezoneOffset();
};

/**
 * Lookup table for timezone abbreviations.
 * Maps IANA timezone identifiers to their standard/daylight abbreviations.
 * Fallback to platform output for unlisted timezones.
 */
const TIMEZONE_ABBREVIATIONS = {
  // Central European
  'Europe/Berlin': { standard: 'CET', daylight: 'CEST' },
  'Europe/Paris': { standard: 'CET', daylight: 'CEST' },
  'Europe/Amsterdam': { standard: 'CET', daylight: 'CEST' },
  'Europe/Brussels': { standard: 'CET', daylight: 'CEST' },
  'Europe/Vienna': { standard: 'CET', daylight: 'CEST' },
  'Europe/Zurich': { standard: 'CET', daylight: 'CEST' },
  'Europe/Rome': { standard: 'CET', daylight: 'CEST' },
  'Europe/Madrid': { standard: 'CET', daylight: 'CEST' },
  'Europe/Warsaw': { standard: 'CET', daylight: 'CEST' },
  'Europe/Prague': { standard: 'CET', daylight: 'CEST' },
  'Europe/Stockholm': { standard: 'CET', daylight: 'CEST' },
  'Europe/Oslo': { standard: 'CET', daylight: 'CEST' },
  'Europe/Copenhagen': { standard: 'CET', daylight: 'CEST' },
  // UK / Western European
  'Europe/London': { standard: 'GMT', daylight: 'BST' },
  'Europe/Dublin': { standard: 'GMT', daylight: 'IST' },
  'Europe/Lisbon': { standard: 'WET', daylight: 'WEST' },
  // Eastern European
  'Europe/Helsinki': { standard: 'EET', daylight: 'EEST' },
  'Europe/Athens': { standard: 'EET', daylight: 'EEST' },
  'Europe/Bucharest': { standard: 'EET', daylight: 'EEST' },
  'Europe/Sofia': { standard: 'EET', daylight: 'EEST' },
  // US timezones
  'America/New_York': { standard: 'EST', daylight: 'EDT' },
  'America/Chicago': { standard: 'CST', daylight: 'CDT' },
  'America/Denver': { standard: 'MST', daylight: 'MDT' },
  'America/Los_Angeles': { standard: 'PST', daylight: 'PDT' },
  // No DST
  UTC: { standard: 'UTC', daylight: 'UTC' },
};

/**
 * Gets the timezone abbreviation for a specific date.
 * Uses lookup table for known timezones, falls back to platform output.
 *
 * @param {Date} date - The date to get abbreviation for
 * @returns {string} - Timezone abbreviation (e.g., "CET", "CEST")
 */
export const getTimezoneAbbreviation = (date) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const mapping = TIMEZONE_ABBREVIATIONS[tz];

  if (mapping) {
    const dst = isDaylightSavingTime(date);
    return dst ? mapping.daylight : mapping.standard;
  }

  // Fallback: use platform's short format
  return new Intl.DateTimeFormat('en', { timeZoneName: 'short' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value || tz;
};

/**
 * Gets timezone display info for a specific date (or current date if not provided).
 * This is important for DST-aware display: April dates show "CEST", winter dates "CET".
 *
 * @param {string} locale - Locale to use for formatting (default: 'en')
 * @param {Date} [targetDate] - The date to get timezone info for (default: now)
 * @returns {{longName: string, shortName: string, isDST: boolean}}
 */
export const getTimezoneDisplayInfo = (locale = 'en', targetDate = null) => {
  const dateForTz = targetDate ? new Date(targetDate) : new Date();

  // Get long generic name (e.g., "Central European Time")
  const longName = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'longGeneric',
  })
    .formatToParts(dateForTz)
    .find((p) => p.type === 'timeZoneName')?.value;

  // Get short abbreviation using our lookup table (e.g., "CET" or "CEST")
  const shortName = getTimezoneAbbreviation(dateForTz);

  // Fallback if longGeneric is not supported or returns same as short
  const fallbackLong = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return {
    longName: longName || fallbackLong,
    shortName,
    isDST: isDaylightSavingTime(dateForTz),
  };
};

/**
 * Formats time range with timezone information
 */
export const formatTimeRange = (startTime, endTime, locale = 'en') => {
  const startDate = new Date(startTime);
  const endDate = new Date(endTime);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format start and end without tz
  const timeOpts = {
    timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true,
  };
  const startShort = startDate.toLocaleTimeString(locale, timeOpts);
  const endShort = endDate.toLocaleTimeString(locale, timeOpts);

  // Get timezone abbreviation (last token of full format)
  const tzFull = startDate.toLocaleTimeString(locale, { ...timeOpts, timeZoneName: 'short' });
  const tzAbbrev = tzFull.split(' ').pop();

  return {
    timeRange: `${startShort}–${endShort}`,
    timezone: tzAbbrev,
  };
};

/**
 * Formats a date for display
 */
export const formatSlotDate = (date) => dayjs(date).format('ddd, DD MMM YYYY');

/**
 * Groups tasks by module for display
 */
export const groupTasksByModule = (taskIds, taskMap, moduleMap) => {
  const grouped = {};

  taskIds.forEach((taskId) => {
    const task = taskMap[taskId];
    if (!task) return;

    const moduleTitle = moduleMap[task.moduleId] || task.moduleId;
    if (!grouped[moduleTitle]) {
      grouped[moduleTitle] = [];
    }
    grouped[moduleTitle].push(task);
  });

  return grouped;
};

/**
 * Handles clipboard operations with visual feedback
 */
export const createClipboardHandler = (setCopiedItems) => {
  const copyToClipboard = async (text, itemKey) => {
    try {
      await Clipboard.setStringAsync(text);
      setCopiedItems((prev) => new Set([...prev, itemKey]));

      // Reset after 2 seconds
      setTimeout(() => {
        setCopiedItems((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemKey);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      // Silently fail - no toast message
    }
  };

  return { copyToClipboard };
};

/**
 * Filters and categorizes bookings
 */
export const categorizeBookings = (bookings) => {
  const active = bookings.filter((booking) => booking.status !== 'cancelled');
  const cancelled = bookings.filter((booking) => booking.status === 'cancelled');

  return { active, cancelled };
};

/**
 * Validates time format (HH:mm)
 */
export const timeValidationPattern = {
  value: /^([01]\d|2[0-3]):([0-5]\d)$/,
  message: 'HH:mm',
};
